import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format, formatDistanceToNow, isSameDay } from "date-fns";
import { 
  CalendarPlus, Check, Download, Pencil, Search, Phone, 
  RefreshCw, CalendarDays
} from "lucide-react";
import { Modal } from "../../components/ui/modal";
import { DateTimePicker } from "../../components/ui/date-picker";
import { cn } from "../../lib/utils";
import { crmApi } from "./api";
import { downloadAppointmentIcs } from "./appointment-ics";
import { appointmentPillCls, initialsOf, avatarColorOf, type CrmAppointment, type Lead } from "./types";
import {
  Select as _Select, SelectContent as _SelectContent, SelectItem as _SelectItem,
  SelectTrigger as _SelectTrigger, SelectValue as _SelectValue,
} from "../../components/ui/select";

const Select = _Select as any;
const SelectContent = _SelectContent as any;
const SelectItem = _SelectItem as any;
const SelectTrigger = _SelectTrigger as any;
const SelectValue = _SelectValue as any;

const localTime = (iso: string) => { 
  const d = new Date(iso); 
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); 
};

function appointmentTab(row: CrmAppointment, now = new Date()): string {
  if (row.status === "Completed" || row.status === "No-show") return row.status;
  return new Date(row.dueAt) < now ? "Overdue" : new Date(row.dueAt).toDateString() === now.toDateString() ? "Today" : "Upcoming";
}

const TABS = ["All", "Upcoming", "Today", "Overdue", "Completed", "No-show"] as const;
const ALL = "__all__";

export function AppointmentsPage() {
  const cache = useQueryClient();
  const navigate = useNavigate();
  const query = useQuery({ queryKey: ["crm-appointments"], queryFn: crmApi.appointments, refetchInterval: 30000 });
  const leads = useQuery({ queryKey: ["crm-appointment-leads"], queryFn: crmApi.leads });
  const team = useQuery({ queryKey: ["crm-team"], queryFn: crmApi.team });

  const [filter, setFilter] = useState<string>("All");
  const [search, setSearch] = useState("");
  const [agentFilter, setAgentFilter] = useState(ALL);
  const [editing, setEditing] = useState<CrmAppointment | "new" | null>(null);
  const [leadId, setLeadId] = useState("");
  const [due, setDue] = useState("");
  const [note, setNote] = useState("");
  const [status, setStatus] = useState("Scheduled");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const rows = useMemo(() => query.data || [], [query.data]);
  const allLeads = useMemo(() => leads.data || [], [leads.data]);
  const members = useMemo(() => team.data?.members || [], [team.data?.members]);

  const leadById = useMemo(() => new Map(allLeads.map((l: Lead) => [l.id, l])), [allLeads]);
  const agentById = useMemo(() => new Map(members.map((m: any) => [m.id, m.name])), [members]);

  const overdueCount = rows.filter(r => appointmentTab(r) === "Overdue").length;

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(row => {
      // Tab filter
      if (filter !== "All" && appointmentTab(row) !== filter) return false;

      const lead = leadById.get(row.leadId);
      const agentId = lead?.assignedAgentId || "unassigned";

      // Agent filter
      if (agentFilter !== ALL && agentId !== agentFilter) return false;

      // Search query
      if (q) {
        const leadName = (row.name || lead?.name || "").toLowerCase();
        const phone = (row.phone || lead?.phone || "").toLowerCase();
        const agenda = (row.note || "").toLowerCase();
        const agentName = (agentById.get(agentId) || "").toLowerCase();
        if (!leadName.includes(q) && !phone.includes(q) && !agenda.includes(q) && !agentName.includes(q)) {
          return false;
        }
      }

      return true;
    }).sort((a, b) => a.dueAt.localeCompare(b.dueAt));
  }, [rows, filter, search, agentFilter, leadById, agentById]);

  const refresh = async () => {
    await cache.invalidateQueries({ queryKey: ["crm-appointments"] });
  };

  const open = (row: CrmAppointment | "new") => {
    setEditing(row);
    setLeadId(row === "new" ? "" : row.leadId);
    setDue(row === "new" ? "" : localTime(row.dueAt));
    setNote(row === "new" ? "" : row.note);
    setStatus(row === "new" ? "Scheduled" : row.status);
    setError("");
  };

  const save = async () => {
    if (busy) return;
    setBusy(true); setError("");
    try {
      if (!due || new Date(due).getTime() <= Date.now()) throw new Error("Choose a future time.");
      const iso = new Date(due).toISOString();
      if (editing === "new") {
        if (!leadId) throw new Error("Choose a lead.");
        await crmApi.createAppointment({ leadId, dueAt: iso, note: note.trim() || undefined });
      } else if (editing) {
        await crmApi.updateAppointment(editing.id, { dueAt: iso, note, status });
      }
      await refresh(); 
      setEditing(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to save appointment.");
    } finally {
      setBusy(false);
    }
  };

  const setStatusQuick = async (row: CrmAppointment, next: string) => {
    setBusy(true);
    try { 
      await crmApi.updateAppointment(row.id, { status: next }); 
      await refresh(); 
    } catch (e) { 
      setError(e instanceof Error ? e.message : "Unable to update."); 
    } finally { 
      setBusy(false); 
    }
  };

  return (
    <div className="flex h-full flex-col p-4 sm:p-5 md:p-6 overflow-hidden">
      {/* ── Single Unified Toolbar: Title + Tabs + Search + Agent Filter + Refresh + Book Appointment ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 shrink-0 border-b border-line pb-2.5">
        {/* Left: Title + Status Filter Tabs */}
        <div className="flex items-center overflow-x-auto gap-2.5">
          <div className="flex items-center gap-2 shrink-0 pr-2 border-r border-line">
            <h1 className="text-sm font-semibold text-ink">Appointments</h1>
            <span className="font-mono text-xs rounded-full bg-soft px-2 py-0.5 text-ink-3">
              {rows.length}
            </span>
            {overdueCount > 0 && (
              <span className="font-mono text-xs rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 px-1.5 py-0.5 font-medium">
                {overdueCount} overdue
              </span>
            )}
          </div>

          <div className="flex items-center gap-1">
            {TABS.map(tab => {
              const count = rows.filter(row => tab === "All" || appointmentTab(row) === tab).length;
              const isActive = filter === tab;
              return (
                <button
                  key={tab}
                  onClick={() => setFilter(tab)}
                  className={cn(
                    "flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium transition-colors whitespace-nowrap",
                    isActive 
                      ? "bg-brand-soft text-brand font-semibold shadow-xs" 
                      : "text-ink-3 hover:bg-soft hover:text-ink"
                  )}
                >
                  <span>{tab}</span>
                  <span className={cn(
                    "font-mono text-[10px] px-1.5 py-0.2 rounded",
                    isActive ? "bg-brand/15 text-brand" : "bg-soft text-ink-3"
                  )}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right: Search + Agent Filter + Refresh + Book Appointment Button */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Smart Search */}
          <div className="relative w-52 sm:w-60">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-3" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search lead, phone, agenda..."
              className="w-full rounded-lg border border-line bg-elevated py-1 pl-7 pr-2.5 text-xs text-ink outline-none focus:border-brand transition-colors h-8"
            />
          </div>

          {/* Agent Filter */}
          <Select value={agentFilter} onValueChange={setAgentFilter}>
            <SelectTrigger className="w-[125px] border-line bg-elevated text-xs text-ink-2 h-8">
              <SelectValue placeholder="All agents" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL} className="text-xs">All agents</SelectItem>
              <SelectItem value="unassigned" className="text-xs">Unassigned</SelectItem>
              {members.map((m: any) => (
                <SelectItem key={m.id} value={m.id} className="text-xs">{m.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Refresh */}
          <button 
            onClick={() => void refresh()} 
            title="Refresh appointments" 
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-line bg-elevated text-ink-2 hover:bg-soft hover:text-ink transition-colors"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>

          {/* Book Appointment Button */}
          <button 
            onClick={() => open("new")} 
            className="flex items-center gap-1.5 rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-hover shadow-xs transition-colors h-8 whitespace-nowrap"
          >
            <CalendarPlus className="h-3.5 w-3.5" /> Book appointment
          </button>
        </div>
      </div>

      {error && <p role="alert" className="mt-2 text-xs text-red-600">{error}</p>}
      {query.error && <p role="alert" className="mt-2 text-xs text-red-600">{(query.error as Error).message}</p>}

      {/* ── Table View ──────────────────────────────────────────── */}
      {query.isLoading ? (
        <div className="flex flex-1 items-center justify-center py-16 text-xs text-ink-3">
          Loading appointments…
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="mt-4 flex flex-col items-center justify-center flex-1 rounded-xl border border-line bg-elevated p-12 text-center">
          <CalendarDays className="h-8 w-8 text-ink-4 mb-2" />
          <p className="text-sm font-semibold text-ink">No appointments found</p>
          <p className="max-w-xs text-xs text-ink-3 mt-0.5">
            {rows.length === 0 ? "Book your first appointment with any lead." : "Try adjusting your search query or status filter."}
          </p>
        </div>
      ) : (
        <div className="mt-3 flex-1 overflow-auto rounded-xl border border-line bg-elevated shadow-xs">
          <table className="w-full border-collapse text-left text-xs">
            <thead className="sticky top-0 z-10 bg-soft text-[11px] font-semibold uppercase tracking-wider text-ink-3 border-b border-line">
              <tr>
                <th className="whitespace-nowrap px-3.5 py-2.5">Lead / Contact</th>
                <th className="whitespace-nowrap px-3.5 py-2.5">When</th>
                <th className="whitespace-nowrap px-3.5 py-2.5">Agenda</th>
                <th className="whitespace-nowrap px-3.5 py-2.5">Assigned Agent</th>
                <th className="whitespace-nowrap px-3.5 py-2.5">Status</th>
                <th className="whitespace-nowrap px-3.5 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line/60">
              {filteredRows.map(row => {
                const lead = leadById.get(row.leadId);
                const leadName = row.name || lead?.name || "Unnamed lead";
                const phone = row.phone || lead?.phone || "";
                const agentName = lead?.assignedAgentId ? agentById.get(lead.assignedAgentId) || "Agent" : "Unassigned";
                const dueDate = new Date(row.dueAt);
                const isOverdue = appointmentTab(row) === "Overdue";
                const isToday = isSameDay(dueDate, new Date());

                return (
                  <tr 
                    key={row.id} 
                    className="hover:bg-soft/60 cursor-pointer transition-colors group"
                    onClick={() => navigate(`/crm/leads/${row.leadId}`)}
                  >
                    {/* Lead info */}
                    <td className="px-3.5 py-2.5">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className={cn(
                          "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white",
                          avatarColorOf(leadName)
                        )}>
                          {initialsOf(leadName)}
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-ink group-hover:text-brand transition-colors">
                            {leadName}
                          </p>
                          {phone && (
                            <div className="flex items-center gap-1.5 text-[11px] text-ink-3">
                              <span>{phone}</span>
                              <a 
                                href={`tel:${phone}`}
                                onClick={e => e.stopPropagation()}
                                title="Call contact"
                                className="text-ink-4 hover:text-brand"
                              >
                                <Phone className="h-2.5 w-2.5" />
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* When */}
                    <td className="whitespace-nowrap px-3.5 py-2.5 text-ink">
                      <div className="space-y-0.5">
                        <div className="font-medium">
                          {format(dueDate, "MMM d, yyyy · h:mm a")}
                        </div>
                        <div className="text-[11px] text-ink-3 flex items-center gap-1">
                          {isToday ? (
                            <span className="text-brand font-semibold">Today</span>
                          ) : (
                            <span>{formatDistanceToNow(dueDate, { addSuffix: true })}</span>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Agenda */}
                    <td className="px-3.5 py-2.5 max-w-[220px]">
                      <p className="truncate text-ink-2" title={row.note || "No agenda"}>
                        {row.note || "—"}
                      </p>
                    </td>

                    {/* Assigned Agent */}
                    <td className="whitespace-nowrap px-3.5 py-2.5">
                      <div className="flex items-center gap-1.5 text-ink-2">
                        <span className={cn(
                          "flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[8px] font-bold text-white",
                          avatarColorOf(agentName)
                        )}>
                          {initialsOf(agentName)}
                        </span>
                        <span className="truncate max-w-[120px]">{agentName}</span>
                      </div>
                    </td>

                    {/* Status badge */}
                    <td className="whitespace-nowrap px-3.5 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-semibold", appointmentPillCls(row.status))}>
                          {row.status}
                        </span>
                        {isOverdue && (
                          <span className="rounded-full bg-red-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-red-600">
                            Overdue
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="whitespace-nowrap px-3.5 py-2.5 text-right" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <button 
                          onClick={() => downloadAppointmentIcs(row)} 
                          title="Download Calendar .ics" 
                          className="rounded p-1 text-ink-3 hover:bg-soft hover:text-ink transition-colors"
                        >
                          <Download className="h-3.5 w-3.5" />
                        </button>
                        
                        {(row.status === "Scheduled" || row.status === "Rescheduled") && (
                          <>
                            <button 
                              onClick={() => open(row)} 
                              title="Reschedule" 
                              className="rounded p-1 text-ink-3 hover:bg-soft hover:text-ink transition-colors"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button 
                              disabled={busy} 
                              onClick={() => void setStatusQuick(row, "Completed")} 
                              title="Mark as completed"
                              className="inline-flex items-center gap-1 rounded bg-emerald-500/10 px-2 py-1 text-[11px] font-semibold text-emerald-600 hover:bg-emerald-500/20 disabled:opacity-40 transition-colors"
                            >
                              <Check className="h-3 w-3" /> Done
                            </button>
                            <button 
                              disabled={busy} 
                              onClick={() => void setStatusQuick(row, "No-show")} 
                              title="Mark as no-show"
                              className="rounded bg-soft px-2 py-1 text-[11px] font-medium text-ink-3 hover:bg-line hover:text-ink disabled:opacity-40 transition-colors"
                            >
                              No-show
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Booking / Rescheduling Modal ────────────────────────── */}
      {editing && (
        <Modal
          title={editing === "new" ? "Book appointment" : "Reschedule appointment"}
          onClose={() => !busy && setEditing(null)}
          width="max-w-md"
          footer={
            <>
              <button 
                type="button" 
                onClick={() => !busy && setEditing(null)} 
                className="rounded-lg px-4 py-2 text-xs font-semibold text-ink-2 hover:bg-soft transition-colors"
              >
                Cancel
              </button>
              <button 
                disabled={busy} 
                onClick={() => void save()} 
                className="rounded-lg bg-brand px-4 py-2 text-xs font-semibold text-white hover:bg-brand-hover disabled:opacity-50 transition-colors shadow-xs"
              >
                {busy ? "Saving…" : editing === "new" ? "Book" : "Save"}
              </button>
            </>
          }
        >
          <div className="flex flex-col gap-3.5">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-ink-2">Lead</label>
              {editing === "new" ? (
                <Select value={leadId} onValueChange={setLeadId}>
                  <SelectTrigger className="w-full border-line bg-transparent px-3 py-2 text-xs text-ink h-9">
                    <SelectValue placeholder="Choose contact" />
                  </SelectTrigger>
                  <SelectContent>
                    {allLeads.map(l => (
                      <SelectItem key={l.id} value={l.id} className="text-xs focus:bg-soft focus:text-ink">
                        {[l.name, l.phone].filter(Boolean).join(" · ") || l.contactId || l.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-xs font-semibold text-ink px-1 py-1">
                  {editing.name || editing.phone || "Selected lead"}
                </p>
              )}
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-ink-2">Date &amp; time</label>
              <DateTimePicker 
                value={due} 
                onChange={setDue} 
                placeholder="Pick date & time"
                className="w-full" 
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-ink-2">Agenda (optional)</label>
              <input 
                maxLength={2000} 
                value={note} 
                onChange={e => setNote(e.target.value)} 
                placeholder="e.g. Product demo, contract discussion..." 
                className="w-full rounded-lg border border-line bg-transparent px-3 py-2 text-xs text-ink outline-none focus:border-brand" 
              />
            </div>
            {editing !== "new" && (
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-ink-2">Status</label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="w-full border-line bg-transparent px-3 py-2 text-xs text-ink h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {["Scheduled", "Completed", "No-show", "Rescheduled"].map(s => (
                      <SelectItem key={s} value={s} className="text-xs focus:bg-soft focus:text-ink">
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {error && <p role="alert" className="text-xs font-semibold text-red-500">{error}</p>}
          </div>
        </Modal>
      )}
    </div>
  );
}
